import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle, LogOut, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/Button'

interface SessionTimeoutWarningProps {
  show: boolean
  timeLeft: number // segundos
  onExtendSession: () => void
  onLogout: () => void
}

export function SessionTimeoutWarning({
  show,
  timeLeft,
  onExtendSession,
  onLogout,
}: SessionTimeoutWarningProps) {
  const [progress, setProgress] = useState(100)

  const totalWarningTime = 5 * 60 // 5 minutos em segundos
  const minutes = Math.floor(timeLeft / 60)
  const seconds = timeLeft % 60
  const formattedTime = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`

  useEffect(() => {
    if (show && timeLeft > 0) {
      setProgress((timeLeft / totalWarningTime) * 100)
    }
  }, [show, timeLeft])

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="glass-card p-8 rounded-2xl max-w-md w-full mx-4 border border-amber-500/20"
          >
            {/* Icon */}
            <div className="w-16 h-16 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle size={28} className="text-amber-400" />
            </div>

            {/* Title */}
            <h3 className="text-lg font-black text-text-primary uppercase tracking-wider text-center mb-2">
              Sessão Prestes a Expirar
            </h3>

            {/* Description */}
            <p className="text-sm text-text-muted text-center mb-6">
              Por segurança, sua sessão será encerrada em breve devido à inatividade.
              Clique em "Continuar" para manter sua sessão ativa.
            </p>

            {/* Timer */}
            <div className="text-center mb-6">
              <span className="text-4xl font-black text-amber-400 tabular-nums tracking-wider">
                {formattedTime}
              </span>
            </div>

            {/* Progress bar */}
            <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden mb-8">
              <motion.div
                className="h-full rounded-full"
                style={{
                  background: progress > 50
                    ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                    : progress > 25
                      ? 'linear-gradient(90deg, #f97316, #f59e0b)'
                      : 'linear-gradient(90deg, #ef4444, #f97316)',
                  boxShadow: `0 0 12px ${progress > 50 ? '#f59e0b' : progress > 25 ? '#f97316' : '#ef4444'}40`,
                }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 1, ease: 'linear' }}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="md"
                icon={<LogOut size={16} />}
                onClick={onLogout}
                className="flex-1"
              >
                Sair Agora
              </Button>
              <Button
                variant="primary"
                size="md"
                icon={<RefreshCw size={16} />}
                onClick={onExtendSession}
                className="flex-1"
              >
                Continuar
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
